"use client";

import React from "react";
import Image from "next/image";
import seminar from "../public/Seminar.webp";

const DEFAULT_TARGET_DATE = "2026-06-28T11:00:00+05:30";
const SEMINAR_ID = "neet-ug-2026-summit";

function parseSeminarDateTime(date, time) {
  if (!date || !time) {
    return new Date(DEFAULT_TARGET_DATE).getTime();
  }

  const cleanTime = String(time)
    .replace(/\s*IST\s*$/i, "")
    .trim();

  const parsedDate = new Date(`${date} ${cleanTime} GMT+0530`);

  if (Number.isNaN(parsedDate.getTime())) {
    return new Date(DEFAULT_TARGET_DATE).getTime();
  }

  return parsedDate.getTime();
}

function getSeminarConfigUrl() {
  const baseUrl = process.env.NEXT_PUBLIC_ADMIN_URL || "";

  return `${baseUrl.replace(/\/+$/, "")}/api/seminar-config/${SEMINAR_ID}`;
}

export default function CreativeSeminarBanner({ onClose, onOpenForm }) {
  const [targetDate, setTargetDate] = React.useState(
    new Date(DEFAULT_TARGET_DATE).getTime()
  );

  const [timeLeft, setTimeLeft] = React.useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  React.useEffect(() => {
    let isMounted = true;

    async function fetchSeminarConfig() {
      try {
        const apiUrl = getSeminarConfigUrl();

        const res = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store",
        });

        const json = await res.json();

        if (!res.ok) {
          console.error("Seminar config API error:", res.status, json);
          return;
        }

        const cfg = json?.data || json;

        if (!isMounted) return;

        if (cfg?.date && cfg?.time) {
          const dynamicTargetDate = parseSeminarDateTime(cfg.date, cfg.time);
          setTargetDate(dynamicTargetDate);
        }
      } catch (error) {
        console.error("Seminar config fetch error:", error);
      }
    }

    fetchSeminarConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const diff = targetDate - now;

      if (diff <= 0) {
        setTimeLeft({
          days: "00",
          hours: "00",
          minutes: "00",
          seconds: "00",
        });
        return;
      }

      const days = String(
        Math.floor(diff / (1000 * 60 * 60 * 24))
      ).padStart(2, "0");

      const hours = String(
        Math.floor((diff / (1000 * 60 * 60)) % 24)
      ).padStart(2, "0");

      const minutes = String(
        Math.floor((diff / (1000 * 60)) % 60)
      ).padStart(2, "0");

      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(
        2,
        "0"
      );

      setTimeLeft({
        days,
        hours,
        minutes,
        seconds,
      });
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <>
      <div
        className="banner-container relative w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-1 top-1 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-xl font-bold text-[#ff9245] shadow hover:bg-white sm:right-3 sm:top-3 sm:h-8 sm:w-8"
        >
          ✕
        </button>

        {/* Image */}
        <div className="relative cursor-pointer" onClick={onOpenForm}>
          <Image
            src={seminar}
            alt="NEET UG 2026 Admission Summit"
            priority
            className="h-auto w-full object-contain"
          />

          {/* Small Dynamic Timer */}
          <div className="countdown-wrap absolute left-1/2 top-2 z-10 -translate-x-1/2">
            <div className="countdown-box">
              <span>{timeLeft.days}d</span>
              <span>{timeLeft.hours}h</span>
              <span>{timeLeft.minutes}m</span>
              <span>{timeLeft.seconds}s</span>
            </div>
          </div>
        </div>

        {/* Animated Register Button */}
        <div className="bg-white p-4">
          <button
            type="button"
            onClick={onOpenForm}
            className="popup-btn w-full rounded-xl bg-[#ff9245] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#ff9245]/90 focus:outline-none"
          >
            Register Now
          </button>
        </div>
      </div>

      <style jsx>{`
        .banner-container {
          transform-origin: top;
          animation: bannerRollout 1.4s cubic-bezier(0.22, 1, 0.36, 1)
            forwards;
        }

        .countdown-wrap {
          animation: fadeDown 0.6s ease forwards;
          opacity: 0;
        }

        .countdown-box {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.92);
          color: #ff9245;
          font-size: 16px;
          font-weight: 800;
          line-height: 1;
          white-space: nowrap;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .popup-btn {
          position: relative;
          overflow: hidden;
          transform: scale(0);
          animation: popIn 0.6s ease forwards, pulseBorder 1.8s infinite;
          animation-delay: 1.2s, 2s;
        }

        .popup-btn::after {
          content: "";
          position: absolute;
          top: 0;
          left: -120%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            120deg,
            transparent,
            rgba(255, 255, 255, 0.5),
            transparent
          );
          transition: left 0.6s ease;
        }

        .popup-btn:hover::after {
          left: 120%;
        }

        @keyframes bannerRollout {
          0% {
            transform: scaleY(0);
            opacity: 0;
          }
          50% {
            transform: scaleY(1.05);
          }
          70% {
            transform: scaleY(0.98);
          }
          100% {
            transform: scaleY(1);
            opacity: 1;
          }
        }

        @keyframes popIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          60% {
            transform: scale(1.1);
            opacity: 1;
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes pulseBorder {
          0% {
            box-shadow: 0 0 0 0 rgba(255, 146, 69, 0.75);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(255, 146, 69, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(255, 146, 69, 0);
          }
        }

        @keyframes fadeDown {
          0% {
            opacity: 0;
            transform: translate(-50%, -8px);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }

        @media (max-width: 640px) {
          .countdown-box {
            gap: 4px;
            padding: 3px 7px;
            font-size: 9px;
          }
        }
      `}</style>
    </>
  );
}